'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { ContentProvider, useContent } from '@/contexts/ContentContext';
import {
  ListChecks, PlusCircle, CalendarDays, Settings, Send,
  ChevronDown, Megaphone, Clapperboard, Archive, Share2, Image, Mic, MessageSquare,
} from 'lucide-react';

const NAV_ITEMS = [
  { href: '/content', label: 'Library', icon: ListChecks },
  { href: '/content/vault', label: 'Vault', icon: Archive },
  { href: '/content/assistant', label: 'Assistant', icon: MessageSquare },
  { href: '/content/create', label: 'Create', icon: PlusCircle },
  { href: '/content/studio', label: 'Studio', icon: Clapperboard },
  { href: '/content/revoice', label: 'Revoice', icon: Mic },
  { href: '/content/calendar', label: 'Calendar', icon: CalendarDays },
  { href: '/content/social', label: 'Social', icon: Share2 },
  { href: '/content/queue', label: 'Queue', icon: Send },
  { href: '/content/assets', label: 'Assets', icon: Image },
  { href: '/content/settings', label: 'Settings', icon: Settings },
];

function ContentSidebar() {
  const pathname = usePathname();
  const { projects, selectedProjectId, setSelectedProject } = useContent();

  return (
    <div className="hidden md:flex w-52 border-r border-[rgb(var(--border))]/60 bg-[rgb(var(--background))]/50 flex-col h-full shrink-0">
      <div className="p-4 border-b border-[rgb(var(--border))]/60">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone className="w-4 h-4 text-pink-400" />
          <span className="font-semibold text-sm text-white">Content</span>
        </div>
        <div className="relative">
          <select value={selectedProjectId}
            onChange={e => {
              const p = projects.find(p => p.id === e.target.value);
              if (p) setSelectedProject(p.id, p.slug);
            }}
            className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white appearance-none cursor-pointer focus:border-cyan-500/50 focus:outline-none">
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-gray-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5">
        {NAV_ITEMS.map(item => {
          const active = item.href === '/content'
            ? pathname === '/content'
            : pathname === item.href || pathname.startsWith(item.href + '/');
          return (
            <Link key={item.href} href={item.href}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                active ? 'bg-cyan-500/10 text-cyan-400' : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}>
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

// Mobile (Telegram mini-app): the 208px sidebar ate half a 390px phone —
// on small screens navigation is a horizontally scrollable strip instead.
function MobileContentNav() {
  const pathname = usePathname();
  const { projects, selectedProjectId, setSelectedProject } = useContent();
  return (
    <div className="md:hidden shrink-0 border-b border-[rgb(var(--border))]/60 bg-[rgb(var(--background))]/50">
      <div className="flex items-center gap-2 px-3 pt-2.5">
        <Megaphone className="w-4 h-4 text-pink-400 shrink-0" />
        <select value={selectedProjectId}
          onChange={e => {
            const p = projects.find(p => p.id === e.target.value);
            if (p) setSelectedProject(p.id, p.slug);
          }}
          className="flex-1 min-w-0 bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white appearance-none focus:border-cyan-500/50 focus:outline-none">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <nav className="flex gap-1 overflow-x-auto px-2 py-2">
        {NAV_ITEMS.map(item => {
          const active = item.href === '/content'
            ? pathname === '/content'
            : pathname === item.href || pathname.startsWith(item.href + '/');
          return (
            <Link key={item.href} href={item.href}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs whitespace-nowrap shrink-0 transition-colors ${
                active ? 'bg-cyan-500/10 text-cyan-400' : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}>
              <item.icon className="w-3.5 h-3.5" />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export default function ContentLayout({ children }: { children: React.ReactNode }) {
  return (
    <ContentProvider>
      <div className="flex flex-col md:flex-row h-full">
        <MobileContentNav />
        <ContentSidebar />
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </ContentProvider>
  );
}
